import { useState, useEffect, useCallback, useMemo } from 'react'
import { useRoleAccess } from '@/hooks/useRoleAccess'
import { useOrganization } from '@/contexts/organization-context'
import { api } from '@/services/api'
import { toast } from 'sonner'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Search, Plus, Trash2, Save, Loader2, ShieldAlert, Check } from 'lucide-react'

// Primitive data structure (matches organization.model JSON structure)
interface Primitive {
  id: string  // The primitive identifier (e.g., "revenue", "country")
  name: string  // Display name (same as id in current structure)
  description: string
  type: 'metric' | 'dimension'
  system?: string  // System this belongs to (e.g., "Global", "Adjust")
  formula?: string  // For calculated metrics
  unit?: string  // Unit of measurement
  aggregation?: string  // Aggregation method: sum, avg, count, last
  // Track local state
  _isNew?: boolean
  _isModified?: boolean
  _isDeleted?: boolean
  _originalData?: Primitive
}

type TabType = 'metrics' | 'dimensions'

const AGGREGATION_OPTIONS = [
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'count', label: 'Count' },
  { value: 'last', label: 'Last' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
]

// Select for System field with create-new capability
function SystemSelect({
  value,
  systems,
  onChange,
}: {
  value: string
  systems: string[]
  onChange: (value: string) => void
}) {
  const [isCreating, setIsCreating] = useState(false)
  const [newSystem, setNewSystem] = useState('')

  if (isCreating) {
    return (
      <div className="flex items-center gap-1">
        <Input
          value={newSystem}
          onChange={(e) => setNewSystem(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newSystem.trim()) {
              onChange(newSystem.trim())
              setNewSystem('')
              setIsCreating(false)
            } else if (e.key === 'Escape') {
              setNewSystem('')
              setIsCreating(false)
            }
          }}
          onBlur={() => {
            if (!newSystem.trim()) {
              setIsCreating(false)
            }
          }}
          className="h-7 text-xs flex-1"
          placeholder="New system..."
          autoFocus
        />
        <button
          type="button"
          className="h-7 px-1.5 hover:bg-accent rounded"
          onClick={() => {
            if (newSystem.trim()) {
              onChange(newSystem.trim())
            }
            setNewSystem('')
            setIsCreating(false)
          }}
        >
          <Check className="h-3 w-3" />
        </button>
      </div>
    )
  }

  return (
    <Select
      value={value || undefined}
      onValueChange={(v) => {
        if (v === '__create_new__') {
          setIsCreating(true)
        } else {
          onChange(v)
        }
      }}
    >
      <SelectTrigger className="h-8 border-transparent hover:border-input focus:border-input bg-transparent text-xs">
        <SelectValue placeholder="Select..." />
      </SelectTrigger>
      <SelectContent>
        {systems.map((system) => (
          <SelectItem key={system} value={system} className="text-xs">
            {system}
          </SelectItem>
        ))}
        <SelectItem value="__create_new__" className="text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Plus className="h-3 w-3" />
            Create new...
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  )
}

export function ModelPage() {
  const { hasSettingsAccess } = useRoleAccess()
  const { currentOrganization } = useOrganization()
  const canAccessModel = hasSettingsAccess()  // owner / admin / manager

  // Data state
  const [primitives, setPrimitives] = useState<Primitive[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // UI state
  const [activeTab, setActiveTab] = useState<TabType>('metrics')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Dialog state
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Load primitives
  const loadPrimitives = useCallback(async () => {
    if (!currentOrganization) return

    setLoading(true)
    try {
      // Request full fields for Model editor (includes system, formula, unit, aggregation)
      const response = await api.get('/data/primitives?fields=full')
      const data = response.data.primitives || []

      // Add tracking properties
      const tracked = data.map((p: Primitive) => ({
        ...p,
        _isNew: false,
        _isModified: false,
        _isDeleted: false,
        _originalData: { ...p },
      }))
      setPrimitives(tracked)
    } catch (error) {
      console.error('Error loading primitives:', error)
      toast.error('Failed to load model data')
    } finally {
      setLoading(false)
    }
  }, [currentOrganization])

  useEffect(() => {
    loadPrimitives()
  }, [loadPrimitives])

  // Filter primitives by tab and search, sorted by Id alphabetically
  const filteredPrimitives = useMemo(() => {
    return primitives.filter((p) => {
      // Filter by type
      const isMetric = activeTab === 'metrics' && p.type === 'metric'
      const isDimension = activeTab === 'dimensions' && p.type === 'dimension'
      if (!isMetric && !isDimension) return false

      // Filter deleted
      if (p._isDeleted) return false

      // Filter by search
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        return (
          p.id?.toLowerCase().includes(term) ||
          p.name?.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term) ||
          p.system?.toLowerCase().includes(term)
        )
      }
      return true
    }).sort((a, b) => (a.id || '').localeCompare(b.id || ''))
  }, [primitives, activeTab, searchTerm])

  // Check for unsaved changes
  const hasChanges = useMemo(() => {
    return primitives.some((p) => p._isNew || p._isModified || p._isDeleted)
  }, [primitives])

  // Get changes summary for dialog
  const changesSummary = useMemo(() => {
    const added = primitives.filter((p) => p._isNew && !p._isDeleted)
    const modified = primitives.filter((p) => p._isModified && !p._isNew && !p._isDeleted)
    const deleted = primitives.filter((p) => p._isDeleted && !p._isNew)
    return { added, modified, deleted }
  }, [primitives])

  // Extract unique systems from all primitives (including new ones)
  const uniqueSystems = useMemo(() => {
    const systems = new Set<string>()
    primitives.forEach((p) => {
      if (p.system && !p._isDeleted) {
        systems.add(p.system)
      }
    })
    return Array.from(systems).sort()
  }, [primitives])

  // Handle cell edit
  const handleCellEdit = (id: string, field: keyof Primitive, value: string) => {
    setPrimitives((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p
        const updated = { ...p, [field]: value }
        // Sync name with id for new primitives
        if (field === 'id' && p._isNew) {
          updated.name = value
        }
        // Mark as modified if not new
        if (!p._isNew) {
          updated._isModified = true
        }
        return updated
      })
    )
  }

  // Handle add new
  const handleAdd = () => {
    const newPrimitive: Primitive = {
      id: `new_${Date.now()}`,
      name: '',
      description: '',
      type: activeTab === 'metrics' ? 'metric' : 'dimension',
      system: 'Custom',
      formula: '',
      unit: '',
      aggregation: 'sum',
      _isNew: true,
      _isModified: false,
      _isDeleted: false,
    }
    setPrimitives((prev) => [newPrimitive, ...prev])
  }

  // Handle selection
  const handleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredPrimitives.map((p) => p.id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  // Handle delete selected
  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) return
    setShowDeleteDialog(true)
  }

  const confirmDelete = () => {
    setPrimitives((prev) =>
      prev.map((p) => {
        if (selectedIds.has(p.id)) {
          return { ...p, _isDeleted: true }
        }
        return p
      }).filter((p) => !(p._isNew && p._isDeleted))
    )
    setSelectedIds(new Set())
    setShowDeleteDialog(false)
    toast.success(`${selectedIds.size} item(s) marked for deletion`)
  }

  // Handle save - sends all changes as a batch update
  const handleSave = async () => {
    if (!currentOrganization) return

    setSaving(true)
    try {
      const { added, modified, deleted } = changesSummary

      // Build the changes payload
      const changes = {
        added: added.map((p) => ({
          id: p.id.startsWith('new_') ? p.name : p.id,  // Use name as id for new items
          type: p.type,
          description: p.description || '',
          system: p.system || 'Custom',
          ...(p.type === 'metric' && {
            formula: p.formula || null,
            unit: p.unit || null,
            aggregation: p.aggregation || 'sum',
          }),
        })),
        modified: modified.map((p) => ({
          id: p.id,
          type: p.type,
          description: p.description || '',
          system: p.system || 'Custom',
          ...(p.type === 'metric' && {
            formula: p.formula || null,
            unit: p.unit || null,
            aggregation: p.aggregation || 'sum',
          }),
        })),
        deleted: deleted.map((p) => ({
          id: p.id,
          type: p.type,
        })),
      }

      // Send batch update
      const response = await api.put('/data/primitives', changes)

      toast.success('Model saved successfully')
      setShowSaveDialog(false)
      // Reload to get fresh data
      await loadPrimitives()
    } catch (error: any) {
      console.error('Error saving model:', error)
      toast.error(`Failed to save: ${error.response?.data?.detail || error.message}`)
    } finally {
      setSaving(false)
    }
  }

  // Restricted to owner / admin / manager
  if (!canAccessModel) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <ShieldAlert className="h-16 w-16 text-muted-foreground" />
        <h2 className="text-xl font-semibold">Access Restricted</h2>
        <p className="text-muted-foreground">
          You need manager, admin, or owner access to use the Model editor.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Model</h2>
          <p className="text-muted-foreground">
            Manage your organization's metrics and dimensions
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteSelected}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete ({selectedIds.size})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleAdd}>
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
          <Button
            size="sm"
            onClick={() => setShowSaveDialog(true)}
            disabled={!hasChanges || saving}
            className={hasChanges ? 'relative' : ''}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save
            {hasChanges && (
              <span className="absolute -top-1 -right-1 h-3 w-3 bg-orange-500 rounded-full" />
            )}
          </Button>
        </div>
      </div>

      {/* Main Card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabType)}>
              <TabsList>
                <TabsTrigger value="metrics">Metrics</TabsTrigger>
                <TabsTrigger value="dimensions">Dimensions</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredPrimitives.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchTerm ? 'No results found' : `No ${activeTab} defined yet`}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={
                          filteredPrimitives.length > 0 &&
                          filteredPrimitives.every((p) => selectedIds.has(p.id))
                        }
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead className="w-[150px]">ID</TableHead>
                    <TableHead className="w-[250px]">Description</TableHead>
                    {activeTab === 'metrics' && (
                      <>
                        <TableHead className="w-[180px]">Formula</TableHead>
                        <TableHead className="w-[80px]">Unit</TableHead>
                        <TableHead className="w-[100px]">Aggregation</TableHead>
                      </>
                    )}
                    <TableHead className="w-[100px]">System</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPrimitives.map((primitive) => {
                    const isSelected = selectedIds.has(primitive.id)
                    const isNew = primitive._isNew
                    const isModified = primitive._isModified

                    return (
                      <TableRow
                        key={primitive.id}
                        className={`
                          ${isNew ? 'bg-green-50 dark:bg-green-950/20' : ''}
                          ${isModified ? 'bg-yellow-50 dark:bg-yellow-950/20' : ''}
                        `}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => handleSelect(primitive.id, checked as boolean)}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={primitive._isNew && primitive.id.startsWith('new_') ? primitive.name : primitive.id}
                            onChange={(e) => {
                              if (isNew) {
                                handleCellEdit(primitive.id, 'name', e.target.value)
                              }
                            }}
                            className="h-8 border-transparent hover:border-input focus:border-input bg-transparent font-mono text-xs"
                            placeholder="metric_name"
                            disabled={!isNew}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={primitive.description || ''}
                            onChange={(e) => handleCellEdit(primitive.id, 'description', e.target.value)}
                            className="h-8 border-transparent hover:border-input focus:border-input bg-transparent text-xs"
                            placeholder="Description"
                          />
                        </TableCell>
                        {activeTab === 'metrics' && (
                          <>
                            <TableCell>
                              <Input
                                value={primitive.formula || ''}
                                onChange={(e) => handleCellEdit(primitive.id, 'formula', e.target.value)}
                                className="h-8 border-transparent hover:border-input focus:border-input bg-transparent font-mono text-xs"
                                placeholder="-"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={primitive.unit || ''}
                                onChange={(e) => handleCellEdit(primitive.id, 'unit', e.target.value)}
                                className="h-8 w-16 border-transparent hover:border-input focus:border-input bg-transparent text-xs"
                                placeholder="-"
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                value={primitive.aggregation || 'sum'}
                                onValueChange={(v) => handleCellEdit(primitive.id, 'aggregation', v)}
                              >
                                <SelectTrigger className="h-8 border-transparent hover:border-input focus:border-input bg-transparent text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {AGGREGATION_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </>
                        )}
                        <TableCell>
                          <SystemSelect
                            value={primitive.system || ''}
                            systems={uniqueSystems}
                            onChange={(v) => handleCellEdit(primitive.id, 'system', v)}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Confirmation Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Changes</DialogTitle>
            <DialogDescription>
              Review the changes before saving to the model.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {changesSummary.added.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-green-600 mb-2">
                  Adding ({changesSummary.added.length})
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {changesSummary.added.slice(0, 5).map((p) => (
                    <li key={p.id}>• {p.name || p.id}</li>
                  ))}
                  {changesSummary.added.length > 5 && (
                    <li>...and {changesSummary.added.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
            {changesSummary.modified.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-yellow-600 mb-2">
                  Modifying ({changesSummary.modified.length})
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {changesSummary.modified.slice(0, 5).map((p) => (
                    <li key={p.id}>• {p.name || p.id}</li>
                  ))}
                  {changesSummary.modified.length > 5 && (
                    <li>...and {changesSummary.modified.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
            {changesSummary.deleted.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-600 mb-2">
                  Deleting ({changesSummary.deleted.length})
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {changesSummary.deleted.slice(0, 5).map((p) => (
                    <li key={p.id}>• {p.name || p.id}</li>
                  ))}
                  {changesSummary.deleted.length > 5 && (
                    <li>...and {changesSummary.deleted.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Confirm Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Items?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedIds.size} item(s) will be marked for deletion.
              This action will be applied when you save the changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default ModelPage
